// Translates between the dialect the new (Lightspeed) planner stores saved-filter
// params in (camelCase keys, '1'/'0' booleans) and the one this composable uses,
// driven by an `interop` config ({ aliases, booleanKeys, projectTypeKeys? }) or
// null to disable.

// `aliases` maps the new planner's key to this planner's; `booleanKeys` lists
// keys whose stored value coerces to a boolean on read.
export const WORKLOAD_SAVED_FILTER_INTEROP = {
  aliases: {
    userIds: 'userids',
    teamIds: 'teamids',
    companyIds: 'companyids',
    projectIds: 'projectids',
    projectTagIds: 'tagids',
    taskTagIds: 'tasktagids',
    useLoggedTime: 'includeLoggedTime',
  },
  booleanKeys: [
    'includeLoggedTime',
    'includeCompletedTasks',
    'includeCalendarEvents',
    'matchAllSkills',
    'matchAllProjectTags',
    'matchAllExcludedTags',
  ],
};

// `projectTypeKeys` is value-mapped, not key-aliased: the new planner stores a
// project-type enum ('' | 'tentative' | 'normal') where this planner stores an
// include-tentative toggle. Tentative-only is unrepresentable in the toggle.
export const WORKLOAD_BACKLOG_SAVED_FILTER_INTEROP = {
  aliases: {
    projectIds: 'projectids',
    tagIds: 'tagids',
    projectCompanyIds: 'companyids',
    assigneeTeamIds: 'teamids',
  },
  booleanKeys: ['includeTentativeProjects', 'matchAllExcludedTags'],
  projectTypeKeys: { enum: 'projectType', toggle: 'includeTentativeProjects' },
};

/**
 * Coerces the new planner's boolean encodings to the 'YES'/'NO' this composable expects.
 * @param {{ booleanKeys: string[] }|null} interop Interop config, or null to disable.
 * @param {string} key Parameter name.
 * @param {any} value Stored value.
 * @returns {any} 'YES'/'NO' for known boolean keys, otherwise the value unchanged.
 */
export function coerceInteropBoolean(interop, key, value) {
  if (!interop?.booleanKeys.includes(key)) {
    return value;
  }
  if (value === true || value === 'true' || value === '1' || value === 1) {
    return 'YES';
  }
  if (value === false || value === 'false' || value === '0' || value === 0) {
    return 'NO';
  }
  return value;
}

/**
 * Folds the new planner's camelCase keys onto the keys this composable reads;
 * the native key wins when both are present, and the camelCase key is dropped.
 * @param {{ aliases: Record<string, string> }|null} interop Interop config, or null to disable.
 * @param {Record<string, any>} apiParams Stored parameters.
 * @returns {Record<string, any>} Parameters keyed for this composable's reader.
 */
export function applyInteropAliasesForRead(interop, apiParams) {
  if (!interop) {
    return apiParams;
  }
  const aliased = { ...apiParams };
  Object.entries(interop.aliases).forEach(([newKey, legacyKey]) => {
    if (!(newKey in aliased)) {
      return;
    }
    if (!(legacyKey in aliased)) {
      aliased[legacyKey] = aliased[newKey];
    }
    delete aliased[newKey];
  });
  if (interop.projectTypeKeys && interop.projectTypeKeys.enum in aliased) {
    const { enum: enumKey, toggle } = interop.projectTypeKeys;
    if (!(toggle in aliased)) {
      // 'tentative' also folds to 'YES' — the toggle can only include or exclude.
      aliased[toggle] = aliased[enumKey] === 'normal' ? 'NO' : 'YES';
    }
    // The enum is kept, not dropped: this planner has no control for tentative-only,
    // so carrying it through is what stops a save here from downgrading it.
  }
  return aliased;
}

/**
 * Emits the new planner's camelCase spelling alongside each legacy key this
 * composable writes, so a record saved here stays legible to it.
 * @param {{ aliases: Record<string, string> }|null} interop Interop config, or null to disable.
 * @param {Record<string, any>} apiFriendlyParams Parameters in this composable's write shape.
 * @returns {Record<string, any>} Parameters carrying both spellings for mapped keys.
 */
export function applyInteropAliasesForWrite(interop, apiFriendlyParams) {
  if (!interop) {
    return apiFriendlyParams;
  }
  const result = { ...apiFriendlyParams };
  Object.entries(interop.aliases).forEach(([newKey, legacyKey]) => {
    if (legacyKey in result) {
      result[newKey] = result[legacyKey];
    }
  });
  if (interop.projectTypeKeys && interop.projectTypeKeys.toggle in result) {
    const { enum: enumKey, toggle } = interop.projectTypeKeys;
    // The toggle is already 'YES'/'NO' here. Only rewrite the enum when it
    // contradicts the toggle, so a tentative-only filter survives a save here.
    if (result[toggle] === 'NO') {
      result[enumKey] = 'normal';
    } else if (result[enumKey] !== 'tentative') {
      result[enumKey] = '';
    }
  }
  return result;
}
